/**
 * Schema Data Fetching Hooks
 *
 * Hooks for fetching and managing schema data in components.
 */

import { useState, useEffect, useCallback, useRef } from "react";
import { useAuthFetch } from "../../../utils/useAuthFetch";
import type {
  SchemaTableWithColumns,
  SchemaCreate,
  SchemaUpdate,
} from "../types";
import {
  subscribeWorkspaceObjectChanges,
  workspaceChangeAffects,
} from "../services/workspaceObjectChanges";

const SCHEMAS_API_BASE = "/api/workspace/myvault/schemas/tables";

/**
 * Helper to create headers with workspace ID
 */
const getWorkspaceHeaders = (workspaceId: string): Record<string, string> => ({
  "x-workspace-id": workspaceId,
});

/**
 * Hook to fetch all schemas for a workspace.
 * Returns { schemas, loading, error, refresh }
 */
export const useSchemas = (workspaceId: string) => {
  const [schemas, setSchemas] = useState<SchemaTableWithColumns[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const authFetch = useAuthFetch();
  const authFetchRef = useRef(authFetch);

  useEffect(() => {
    authFetchRef.current = authFetch;
  }, [authFetch]);

  const fetchSchemas = useCallback(async () => {
    if (!workspaceId) {
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      const response = await authFetchRef.current(
        SCHEMAS_API_BASE,
        "fetch schemas",
        { headers: getWorkspaceHeaders(workspaceId) },
      );
      setSchemas(response.items || response || []);
      setError(null);
    } catch (err) {
      console.error("Schemas fetch error:", err);
      const message =
        err instanceof Error ? err.message : "Failed to fetch schemas";
      setError(message);
    } finally {
      setLoading(false);
    }
  }, [workspaceId]);

  useEffect(() => {
    fetchSchemas();
  }, [fetchSchemas]);

  useEffect(
    () =>
      subscribeWorkspaceObjectChanges(workspaceId, (change) => {
        if (workspaceChangeAffects(change, ["table"])) fetchSchemas();
      }),
    [workspaceId, fetchSchemas],
  );

  return {
    schemas,
    loading,
    error,
    refresh: fetchSchemas,
  };
};

/**
 * Hook to fetch a single schema by ID.
 */
export const useSchema = (workspaceId: string, schemaId: string | null) => {
  const [schema, setSchema] = useState<SchemaTableWithColumns | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const authFetch = useAuthFetch();
  const authFetchRef = useRef(authFetch);

  useEffect(() => {
    authFetchRef.current = authFetch;
  }, [authFetch]);

  const fetchSchema = useCallback(async () => {
    if (!workspaceId || schemaId === null) {
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      const response = await authFetchRef.current(
        `${SCHEMAS_API_BASE}/${schemaId}`,
        "fetch schema",
        { headers: getWorkspaceHeaders(workspaceId) },
      );
      setSchema(response);
      setError(null);
    } catch (err) {
      console.error("Schema fetch error:", err);
      const message =
        err instanceof Error ? err.message : "Failed to fetch schema";
      setError(message);
    } finally {
      setLoading(false);
    }
  }, [workspaceId, schemaId]);

  useEffect(() => {
    fetchSchema();
  }, [fetchSchema]);

  useEffect(
    () =>
      subscribeWorkspaceObjectChanges(workspaceId, (change) => {
        if (workspaceChangeAffects(change, ["table"], schemaId)) fetchSchema();
      }),
    [workspaceId, schemaId, fetchSchema],
  );

  return {
    schema,
    loading,
    error,
    refresh: fetchSchema,
  };
};

/**
 * Hook to fetch columns for a specific table.
 */
export const useSchemaColumns = (
  workspaceId: string,
  tableId: string | null,
) => {
  const [columns, setColumns] = useState<
    import("../types").SchemaColumnResponse[]
  >([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const authFetch = useAuthFetch();
  const authFetchRef = useRef(authFetch);

  useEffect(() => {
    authFetchRef.current = authFetch;
  }, [authFetch]);

  const fetchColumns = useCallback(async () => {
    if (!workspaceId || !tableId) {
      setLoading(false);
      setColumns([]);
      return;
    }

    try {
      setLoading(true);
      const response = await authFetchRef.current(
        `${SCHEMAS_API_BASE}/${tableId}/columns`,
        "fetch columns",
        { headers: getWorkspaceHeaders(workspaceId) },
      );
      setColumns(response || []);
      setError(null);
    } catch (err) {
      console.error("Columns fetch error:", err);
      const message =
        err instanceof Error ? err.message : "Failed to fetch columns";
      setError(message);
    } finally {
      setLoading(false);
    }
  }, [workspaceId, tableId]);

  useEffect(() => {
    fetchColumns();
  }, [fetchColumns]);

  useEffect(
    () =>
      subscribeWorkspaceObjectChanges(workspaceId, (change) => {
        if (workspaceChangeAffects(change, ["table"], tableId)) fetchColumns();
      }),
    [workspaceId, tableId, fetchColumns],
  );

  return {
    columns,
    loading,
    error,
    refresh: fetchColumns,
  };
};

/**
 * Hook for schema mutations (create, update, delete).
 * Returns action functions and mutation state.
 */
export const useSchemaMutations = (workspaceId: string) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const authFetch = useAuthFetch();

  const createSchema = useCallback(
    async (data: SchemaCreate): Promise<SchemaTableWithColumns> => {
      setLoading(true);
      setError(null);
      try {
        const response = await authFetch(SCHEMAS_API_BASE, "create schema", {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            ...getWorkspaceHeaders(workspaceId),
          },
          body: JSON.stringify(data),
        });
        return response;
      } catch (err) {
        const message =
          err instanceof Error ? err.message : "Failed to create schema";
        setError(message);
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [authFetch, workspaceId],
  );

  const updateSchema = useCallback(
    async (
      schemaId: string,
      data: SchemaUpdate,
    ): Promise<SchemaTableWithColumns> => {
      setLoading(true);
      setError(null);
      try {
        const response = await authFetch(
          `${SCHEMAS_API_BASE}/${schemaId}`,
          "update schema",
          {
            method: "PUT",
            headers: {
              "Content-Type": "application/json",
              ...getWorkspaceHeaders(workspaceId),
            },
            body: JSON.stringify(data),
          },
        );
        return response;
      } catch (err) {
        const message =
          err instanceof Error ? err.message : "Failed to update schema";
        setError(message);
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [authFetch, workspaceId],
  );

  const deleteSchema = useCallback(
    async (schemaId: string): Promise<void> => {
      setLoading(true);
      setError(null);
      try {
        await authFetch(`${SCHEMAS_API_BASE}/${schemaId}`, "delete schema", {
          method: "DELETE",
          headers: getWorkspaceHeaders(workspaceId),
        });
      } catch (err) {
        const message =
          err instanceof Error ? err.message : "Failed to delete schema";
        setError(message);
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [authFetch, workspaceId],
  );

  return {
    createSchema,
    updateSchema,
    deleteSchema,
    loading,
    error,
  };
};
